import { Dialog, DialogContent, Typography } from '@mui/material';
import {
  checkFactoringRequestDeletion,
  deleteFactoringRequest,
} from 'lib/api/factoring';
import WarningDeleteDialog from 'lib/common/WarningDeleteDialog';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import DialogTitle from 'lib/designSystem/DialogTitle';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

interface FactoringRequestDeleteConfirmationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  requestId: number;
  setUp: () => void;
}

const FactoringRequestDeleteConfirmationModal = (
  props: FactoringRequestDeleteConfirmationModalProps,
) => {
  const { open, setOpen, requestId, setUp } = props;
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [canDelete, setCanDelete] = useState(false);
  const [reason, setReason] = useState('');

  const checkIfCanDelete = async () => {
    setLoading(true);
    try {
      const response = await checkFactoringRequestDeletion(dispatch, requestId);
      setCanDelete(response.canDelete);
      setReason(response.reason ?? '');
    } catch (error) {
      showAlert(
        dispatch,
        'error',
        'Ha ocurrido un error al intentar verificar si la solicitud de factoring puede ser eliminada.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      void checkIfCanDelete();
    }
  }, [open]);

  if (loading) {
    return (
      <Dialog maxWidth="sm" fullWidth open={open}>
        <DialogTitle setOpen={setOpen}>
          Eliminar solicitud #{requestId}
        </DialogTitle>
        <DialogContent style={{ minHeight: 200 }}>
          <CenteredCircularProgress type="dialog" />
        </DialogContent>
      </Dialog>
    );
  }

  if (!canDelete && reason) {
    return (
      <Dialog maxWidth="sm" fullWidth open={open}>
        <DialogTitle setOpen={setOpen}>
          Eliminar solicitud #{requestId}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="error">
            La solicitud no se puede eliminar porque:
          </Typography>
          <Typography variant="body1" color="error">
            - {reason}
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <WarningDeleteDialog
      open={open}
      setOpen={setOpen}
      title={`Eliminar solicitud #${requestId}`}
      warningMessages={[
        'Las aprobaciones, pagos y conciliaciones de esta solicitud',
        'Montos en garantía y CxP creadas a partir de la activación',
        'Las solicitudes de factoring completadas por documentos de esta solicitud volverán a estado ACTIVO',
        'Las extensiones de factoring y reventas relacionadas volverán a estado ACTIVO',
      ]}
      deleteItem={async (dispatch) =>
        await deleteFactoringRequest(dispatch, requestId)
      }
      onDeleteSuccess={setUp}
      confirmationText="Estoy consciente de que al eliminar esta solicitud no podré recuperarla"
    />
  );
};

export default FactoringRequestDeleteConfirmationModal;
